import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getLocales } from "@companion-core/shared/app/Utils/config";
import { JPInputSelect } from "@companion-core/shared/app/Components/Inputs/JPInputSelect";
import i18next, { loadLanguageAsync } from "@companion-core/shared/app/I18n";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import { getAntdLocale } from "Utilities/lang";
import { sendCookieBannerId } from "Utilities/cookieBanner";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import "@companion-core/web/src/Assets/Styles/scss/languageSwitcher.scss";

const GROUP_LABEL_ID = "language-switcher-group-label";
const COMBOBOX_LABEL_ID = "language-switcher-combobox-label";
const LIVE_REGION_CLEAR_DELAY_MS = 100;

export const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const { setAntdLocale } = useI18nStore();
  const formattedLanguages = getLocales().map((language: string) => ({
    value: language,
    text: `common.languages.${language}`,
  }));
  const { publicConfig } = useConfigStore();
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const pendingLanguageRef = useRef<string | null>(null);
  const announceTimeoutRef = useRef<number | null>(null);
  const announcementSeqRef = useRef(0);

  const announceSelection = (message: string) => {
    const liveRegion = liveRegionRef.current;
    if (!liveRegion) {
      return;
    }

    if (announceTimeoutRef.current !== null) {
      window.clearTimeout(announceTimeoutRef.current);
    }

    announcementSeqRef.current += 1;
    const uniqueSuffix = "\u200B".repeat(announcementSeqRef.current % 5);

    liveRegion.textContent = "";
    announceTimeoutRef.current = window.setTimeout(() => {
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = `${message}${uniqueSuffix}`;
      }
      announceTimeoutRef.current = null;
    }, LIVE_REGION_CLEAR_DELAY_MS);
  };

  useEffect(
    () => () => {
      if (announceTimeoutRef.current !== null) {
        window.clearTimeout(announceTimeoutRef.current);
      }
    },
    [],
  );

  const changeLanguage = async (lang: string) => {
    if (!lang || lang === i18next.language) {
      return;
    }

    pendingLanguageRef.current = lang;
    await loadLanguageAsync(lang);

    if (pendingLanguageRef.current !== lang) {
      return;
    }

    setAntdLocale(getAntdLocale(lang));
    announceSelection(
      t("common.language-switcher.accessibility.selected", {
        language: t(`common.languages.${lang}`),
      }),
    );

    if (publicConfig?.data?.cookie_banner_type === "digital-control-room") sendCookieBannerId();
    window.scrollTo(0, 0); // go to top page after changing language
  };

  const currentLanguageLabel = t(`common.languages.${i18n.language}`);

  return (
    <div className="language-switcher" role="group" aria-labelledby={GROUP_LABEL_ID}>
      <span id={GROUP_LABEL_ID} className="language-switcher__sr-only">
        {t("common.language-switcher.accessibility.group")}
      </span>
      <span id={COMBOBOX_LABEL_ID} className="language-switcher__sr-only">
        {t("common.language-switcher.accessibility.current-language", {
          language: currentLanguageLabel,
        })}
      </span>
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="language-switcher__sr-only"
      />
      <JPInputSelect
        name="language-switcher"
        initialValue={i18n.language}
        options={formattedLanguages}
        dropdownClass="language-switcher__dropdown"
        testID="JPInputSelect__language-switcher"
        ariaLabelledBy={COMBOBOX_LABEL_ID}
        labelRender={(item) => <span aria-hidden="true">{item.label}</span>}
        onValueChange={(lang: string) => {
          void changeLanguage(lang);
        }}
      />
    </div>
  );
};
